import type {
  StaffMemoryCommandOutcome,
  StaffMemoryFact,
  StaffMemoryReviewItem,
} from "@lafayette/schemas";
import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";

import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { Card, CardTitle } from "../../ui/card";
import { CheckboxField } from "../../ui/fields";
import { BackLink } from "../../ui/links";
import { PageHeader } from "../../ui/page-header";
import { StatusMessage } from "../../ui/status-message";
import { useMemoryCapabilities } from "../access";
import { useMemoryCommands, type VersionedCommandName } from "../commands";
import { AcceptDialog } from "../components/AcceptDialog";
import { AddFactDialog } from "../components/AddFactDialog";
import { CorrectDialog } from "../components/CorrectDialog";
import type { FactAction } from "../components/FactCard";
import { FactCommandDialog } from "../components/FactCommandDialog";
import { JumpLink, JumpLinks, WarningNote } from "../components/parts";
import { describeMemoryError } from "../errors";
import { kindLabel, relationshipLabels } from "../labels";
import { useCustomerMemory } from "../queries";
import { useMemoryRuntime } from "../runtime";
import { ActiveSection, DisputedSection, HistorySection } from "../sections/FactSections";
import { openChallenge, type ReviewAction, ReviewSection } from "../sections/ReviewSection";

type Dialog =
  | Readonly<{ kind: "add" }>
  | Readonly<{ kind: "fact"; command: VersionedCommandName; fact: StaffMemoryFact }>
  | Readonly<{ kind: "correct"; fact: StaffMemoryFact }>
  | Readonly<{ kind: "accept"; candidate: StaffMemoryFact; challenged: StaffMemoryFact }>;

const SUCCESS_TEXT: Readonly<Record<StaffMemoryCommandOutcome["command"], string>> = {
  RECORD: "Memory added.",
  ACTIVATE: "Memory activated. Lafayette can use it now.",
  REJECT: "Candidate rejected. Its value is erased.",
  ACCEPT: "Candidate accepted. It replaced the earlier memory, which moved to history.",
  CONFIRM: "Memory confirmed. Its last-confirmed date is today.",
  DISPUTE: "Memory marked as disputed. Lafayette does not use it until you resolve the dispute.",
  RESOLVE: "Dispute resolved. The memory is active again.",
  CORRECT: "Memory corrected. The earlier value moved to history.",
  DELETE: "Memory deleted. The value is erased and the history stays.",
};

/**
 * M-S2 and the commands M-S4 to M-S12 for one customer. The page reads the workspace, shows the
 * facts by state, and opens one dialog for each action. It offers an action only to a role that may
 * take it, and it handles the server's refusal anyway (the API decides every request again).
 */
export function CustomerMemoryPage() {
  const { customerId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const showHistory = params.get("history") === "1";
  const { now } = useMemoryRuntime();
  const { capabilities, isLoading: rolesLoading } = useMemoryCapabilities();
  const memory = useCustomerMemory(customerId, showHistory, capabilities.read);
  const commands = useMemoryCommands();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const noticeRef = useRef<HTMLParagraphElement>(null);

  // After a command the focus goes to the result sentence: the button that opened the dialog may be gone.
  useEffect(() => {
    if (notice !== null) noticeRef.current?.focus();
  }, [notice]);

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.read) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Your staff role does not include customer memory."
      />
    );
  }
  if (memory.isPending) {
    return <AsyncState kind="loading" title="Loading memory" message="Waiting for the answer." />;
  }
  if (memory.isError) {
    const failure = describeMemoryError(memory.error);
    return failure.kind === "forbidden" ? (
      <AsyncState kind="error" title="You cannot open this" message={failure.message} />
    ) : (
      <AsyncState
        kind="error"
        title="The memory did not load"
        message={failure.message}
        onRetry={() => void memory.refetch()}
      />
    );
  }

  const view = memory.data;
  const canManage = capabilities.manage;
  const name = view.customer.preferredName ?? "this customer";
  const nothingOpen =
    view.active.length === 0 && view.review.length === 0 && view.disputed.length === 0;

  const finish = (outcome: StaffMemoryCommandOutcome) => {
    setDialog(null);
    setNotice(SUCCESS_TEXT[outcome.command]);
  };
  const close = () => {
    setDialog(null);
    commands.reloadAfterClose();
  };

  const onFactAction = (action: FactAction, fact: StaffMemoryFact) => {
    setNotice(null);
    if (action === "correct") setDialog({ kind: "correct", fact });
    else if (action !== "accept") setDialog({ kind: "fact", command: action, fact });
  };
  const onReviewAction = (action: ReviewAction, item: StaffMemoryReviewItem) => {
    setNotice(null);
    if (action === "accept") {
      const challenged = openChallenge(item);
      if (challenged !== null) setDialog({ kind: "accept", candidate: item.candidate, challenged });
    } else {
      setDialog({ kind: "fact", command: action, fact: item.candidate });
    }
  };

  return (
    <section
      className="grid grid-cols-[minmax(0,1fr)] gap-6"
      aria-labelledby="page-title"
      aria-busy={memory.isFetching}
    >
      <PageHeader
        breadcrumbs={<BackLink to="/customers">Back to customers</BackLink>}
        eyebrow="Customer memory"
        title={`What Lafayette remembers about ${name}`}
        description={`Your relationship: ${relationshipLabels[view.relationship]}. Every change is saved with your name and a reason.`}
        actions={
          canManage ? (
            <Button
              onClick={() => {
                setNotice(null);
                setDialog({ kind: "add" });
              }}
            >
              Add a memory
            </Button>
          ) : null
        }
      />

      {notice === null ? null : (
        <StatusMessage message={{ tone: "success", text: notice }} focusRef={noticeRef} />
      )}

      {view.truncated ? (
        <WarningNote>
          Some memories are not shown. This page lists up to 100 facts in each part, and this
          customer has more.
        </WarningNote>
      ) : null}

      <JumpLinks label="Memory sections">
        <JumpLink href="#memory-active">Active ({view.active.length})</JumpLink>
        <JumpLink href="#memory-review">Needs review ({view.review.length})</JumpLink>
        <JumpLink href="#memory-disputed">Disputed ({view.disputed.length})</JumpLink>
        {showHistory ? (
          <JumpLink href="#memory-history">History ({view.history.length})</JumpLink>
        ) : null}
      </JumpLinks>

      <section aria-labelledby="memory-controls-title">
        <Card className="grid gap-3 p-5">
          <div className="grid gap-0.5">
            <CardTitle id="memory-controls-title">Customer controls</CardTitle>
            <p className="m-0 text-sm text-muted-foreground">
              Only the customer can change these. They decide what Lafayette may use.
            </p>
          </div>
          <div className="grid gap-1 text-[15px] leading-[22px]">
            <p className="m-0">
              Personalization:{" "}
              <strong>{view.controls.paused ? "Paused by the customer" : "On"}</strong>
            </p>
            {view.controls.paused ? (
              <WarningNote role="note">
                Lafayette uses none of these memories while personalization is paused.
              </WarningNote>
            ) : null}
            <p className="m-0">
              Kinds switched off:{" "}
              <strong>
                {view.controls.disabledKinds.length === 0
                  ? "none"
                  : view.controls.disabledKinds.map(kindLabel).join(", ")}
              </strong>
            </p>
          </div>
        </Card>
      </section>

      <CheckboxField
        id="memory-show-history"
        label="Show history (replaced, expired and deleted memories)"
        checked={showHistory}
        onChange={(checked) => setParams(checked ? { history: "1" } : {}, { replace: true })}
      />

      {nothingOpen && (!showHistory || view.history.length === 0) ? (
        <AsyncState
          kind="empty"
          title="No memories yet"
          message={`Lafayette does not remember anything about ${name} yet.`}
          accessory={
            canManage ? (
              <div>
                <Button onClick={() => setDialog({ kind: "add" })}>Add a memory</Button>
              </div>
            ) : undefined
          }
        />
      ) : (
        <>
          <ActiveSection
            facts={view.active}
            customerId={customerId}
            now={now()}
            canManage={canManage}
            onAction={onFactAction}
          />
          <ReviewSection
            items={view.review}
            customerId={customerId}
            now={now()}
            canManage={canManage}
            onAction={onReviewAction}
          />
          <DisputedSection
            facts={view.disputed}
            customerId={customerId}
            now={now()}
            canManage={canManage}
            onAction={onFactAction}
          />
          {showHistory ? (
            <HistorySection
              facts={view.history}
              customerId={customerId}
              now={now()}
              canManage={canManage}
              onAction={onFactAction}
            />
          ) : null}
        </>
      )}

      {dialog?.kind === "add" ? (
        <AddFactDialog customerId={customerId} run={commands.run} onDone={finish} onClose={close} />
      ) : null}
      {dialog?.kind === "fact" ? (
        <FactCommandDialog
          command={dialog.command}
          fact={dialog.fact}
          run={commands.run}
          onDone={finish}
          onClose={close}
        />
      ) : null}
      {dialog?.kind === "correct" ? (
        <CorrectDialog fact={dialog.fact} run={commands.run} onDone={finish} onClose={close} />
      ) : null}
      {dialog?.kind === "accept" ? (
        <AcceptDialog
          candidate={dialog.candidate}
          challenged={dialog.challenged}
          now={now()}
          run={commands.run}
          onDone={finish}
          onClose={close}
        />
      ) : null}
    </section>
  );
}
